/**
 * Tenant Experience Intelligence Layer.
 *
 * Pure, runtime-only personality presets keyed by country / tenant slug.
 * Used to color narrative tone, crowd energy, sponsor copy, virality copy,
 * and visual CSS variables — without shipping heavy assets.
 */

export type CrowdEnergy = "calm" | "vivid" | "intense" | "carnival" | "dramatic";
export type CelebrationStyle = "festive" | "epic" | "tactical" | "communal" | "minimal";
export type RivalryStyle = "playful" | "fierce" | "tactical" | "tribal";
export type NarrativeTone = "neutral" | "passionate" | "festive" | "dramatic" | "communal";
export type VisualIntensity = "low" | "medium" | "high" | "max";
export type SoundtrackMode = "off" | "ambient" | "stadium" | "carnival";
export type HypeBehavior = "steady" | "burst" | "wave" | "explosive";
export type UiPersonality = "clean" | "vibrant" | "bold" | "editorial";
export type SponsorStyle = "global" | "local-mx" | "local-br" | "local-ar" | "local-co" | "local-do";

export interface EmotionalProfile {
  crowd_energy: CrowdEnergy;
  celebration_style: CelebrationStyle;
  rivalry_style: RivalryStyle;
  narrative_tone: NarrativeTone;
  visual_intensity: VisualIntensity;
  soundtrack_mode: SoundtrackMode;
  hype_behavior: HypeBehavior;
  ui_personality: UiPersonality;
  sponsor_style: SponsorStyle;
  accent_glow?: string;     // CSS color for stadium glow / pulse
  ticker_intensity?: number; // 0..1
  share_pressure?: number;   // 0..1
}

export const DEFAULT_PROFILE: EmotionalProfile = {
  crowd_energy: "vivid",
  celebration_style: "festive",
  rivalry_style: "playful",
  narrative_tone: "neutral",
  visual_intensity: "medium",
  soundtrack_mode: "ambient",
  hype_behavior: "wave",
  ui_personality: "clean",
  sponsor_style: "global",
  accent_glow: "#3B82F6",
  ticker_intensity: 0.5,
  share_pressure: 0.5,
};

/** Curated country/tenant presets — Phase 2 cultural themes. */
export const COUNTRY_PROFILES: Record<string, EmotionalProfile> = {
  MX: {
    crowd_energy: "intense", celebration_style: "epic", rivalry_style: "fierce",
    narrative_tone: "passionate", visual_intensity: "high", soundtrack_mode: "stadium",
    hype_behavior: "explosive", ui_personality: "bold", sponsor_style: "local-mx",
    accent_glow: "#06B26B", ticker_intensity: 0.85, share_pressure: 0.85,
  },
  BR: {
    crowd_energy: "carnival", celebration_style: "festive", rivalry_style: "playful",
    narrative_tone: "festive", visual_intensity: "high", soundtrack_mode: "carnival",
    hype_behavior: "wave", ui_personality: "vibrant", sponsor_style: "local-br",
    accent_glow: "#F6C300", ticker_intensity: 0.8, share_pressure: 0.75,
  },
  AR: {
    crowd_energy: "dramatic", celebration_style: "tactical", rivalry_style: "fierce",
    narrative_tone: "dramatic", visual_intensity: "max", soundtrack_mode: "stadium",
    hype_behavior: "burst", ui_personality: "editorial", sponsor_style: "local-ar",
    accent_glow: "#74ACDF", ticker_intensity: 0.9, share_pressure: 0.7,
  },
  CO: {
    crowd_energy: "vivid", celebration_style: "communal", rivalry_style: "tribal",
    narrative_tone: "communal", visual_intensity: "high", soundtrack_mode: "stadium",
    hype_behavior: "wave", ui_personality: "vibrant", sponsor_style: "local-co",
    accent_glow: "#FCD116", ticker_intensity: 0.78, share_pressure: 0.85,
  },
  DO: {
    crowd_energy: "carnival", celebration_style: "communal", rivalry_style: "playful",
    narrative_tone: "festive", visual_intensity: "high", soundtrack_mode: "carnival",
    hype_behavior: "explosive", ui_personality: "vibrant", sponsor_style: "local-do",
    accent_glow: "#CE1126", ticker_intensity: 0.82, share_pressure: 0.9,
  },
  GLOBAL: DEFAULT_PROFILE,
};

export function resolveProfile(
  countryCode: string | null | undefined,
  override?: Partial<EmotionalProfile> | null,
): EmotionalProfile {
  const base = COUNTRY_PROFILES[(countryCode || "GLOBAL").toUpperCase()] ?? DEFAULT_PROFILE;
  return { ...DEFAULT_PROFILE, ...base, ...(override ?? {}) };
}

/** Phase 3 — narrative tone transformer. */
export function colorNarrativeText(
  text: string,
  profile: EmotionalProfile,
  countryLabel?: string | null,
): string {
  const subject = countryLabel ?? "El equipo";
  switch (profile.narrative_tone) {
    case "passionate":
      return `🔥 ${subject} está empujando con intensidad brutal. ${text}`;
    case "dramatic":
      return `⚠️ ${subject} está tomando control táctico. ${text}`;
    case "festive":
      return `✨ ${subject} convierte el partido en espectáculo. ${text}`;
    case "communal":
      return `🤝 ${subject} se mueve como una sola voz. ${text}`;
    default:
      return text;
  }
}

/** Phase 5 — localized sponsor copy + key. */
export interface SponsorPick {
  id: string;
  name: string;
  tagline: string;
}

export function pickLocalSponsor(profile: EmotionalProfile): SponsorPick {
  switch (profile.sponsor_style) {
    case "local-mx":
      return { id: "caliente", name: "Caliente", tagline: "Vive el Mundial con la casa de México." };
    case "local-br":
      return { id: "betano-br", name: "Betano", tagline: "O Mundial é maior com você." };
    case "local-ar":
      return { id: "betsson-ar", name: "Betsson", tagline: "Vivilo con pasión argentina." };
    case "local-co":
      return { id: "wplay", name: "Wplay", tagline: "Tu jugada, tu país." };
    case "local-do":
      return { id: "patrocinio-do", name: "Patrocinio Disponible", tagline: "Powered by VerseSport Intelligence." };
    default:
      return { id: "vs-global", name: "VerseSport", tagline: "Tu Mundial en 3D." };
  }
}

/** Phase 7 — localized viral copy. */
export function localizedChallengeMessage(
  profile: EmotionalProfile,
  url: string,
  countryLabel?: string | null,
): string {
  const country = countryLabel ?? "tu país";
  switch (profile.narrative_tone) {
    case "passionate":
      return `🔥 Te reto a vencerme en el Bracket 3D de VerseSport. ¡${country} no se rinde! ${url}`;
    case "festive":
      return `🎉 Bora pra cima! Desafío Mundialista en VerseSport 🇧🇷 ${url}`;
    case "dramatic":
      return `⚽ Decime si te animás. Bracket 3D del Mundial. ${url}`;
    case "communal":
      return `🤝 Súmate a ${country} en VerseSport. Reta a tus panas: ${url}`;
    default:
      return `🏆 Beat me on the VerseSport 3D Bracket: ${url}`;
  }
}

/** Phase 4 — CSS vars driven by the profile (lightweight). */
export function applyExperienceCssVars(profile: EmotionalProfile): void {
  if (typeof document === "undefined") return;
  const r = document.documentElement;
  if (profile.accent_glow) r.style.setProperty("--tenant-accent-glow", profile.accent_glow);
  r.style.setProperty("--tenant-ticker-intensity", String(profile.ticker_intensity ?? 0.5));
  r.style.setProperty("--tenant-share-pressure", String(profile.share_pressure ?? 0.5));
  const energy = {
    calm: 0.3, vivid: 0.6, intense: 0.85, carnival: 0.9, dramatic: 1,
  }[profile.crowd_energy];
  r.style.setProperty("--tenant-crowd-energy", String(energy));
  r.dataset.tenantPersonality = profile.ui_personality;
  r.dataset.tenantSponsorStyle = profile.sponsor_style;
}